/* eslint-disable max-lines -- one definition per contract type, which reads best side by side */
// Declared in a leaf module (type-only imports, besides constants) so the contract types and
// validators can build their helpers on it without an import cycle.
import {
  AXA_DEFAULT_FUND_ISIN,
  AXA_PERECOI_REQUIRED_FUND_ISINS,
  AXA_PREFILLED_FUND_ISINS,
  MALAKOFF_EXTERNAL_FUND_ISINS,
  MALAKOFF_H_DEFAULT_FUND_ISIN,
  MALAKOFF_H_FUND_ISINS,
  MALAKOFF_L_CORE_FUND_ISINS,
  MALAKOFF_L_DEFAULT_FUND_ISIN,
  MALAKOFF_L_FUND_ISINS,
  MALAKOFF_M_DEFAULT_FUND_ISIN,
  MALAKOFF_M_FUND_ISINS,
} from '@Common/constants/employee_savings';
import { DEFAULT_COMPANY_SUPPORT_PHONE_NUMBER } from '@Common/types/b2b/support';

import type { B2bSavingPlanCategory, B2bSavingPlanType } from '@Common/types/b2b/plans';
import type { B2bCashSource } from '@Common/types/bo/b2b/b2b';
import type {
  B2bContractRelationshipTypeValues,
  B2bContractType,
} from '@Common/types/bo/b2b/contracts';
import type { B2bCampaignActionHandler } from '@Common/types/bo/b2b/contracts/campaign_action_handler';
import type { MatchingContributionConfig } from '@Common/types/bo/b2b/matching_contribution_config';
import type {
  B2bCompanyPlanMCPoolType,
  B2bManagedPlanRebalancingFrequency,
} from '@Common/types/bo/b2b/plans';

// A locked value is fixed by the offer: contracts and plans holding another one are rejected
export type FieldRule<T> = { default: T; locked?: true };

// A value that can depend on the insurer of the environment, Shares environments taking the AXA one
export type ByEnvironmentInsurer<T> = T | { axa: T; malakoff: T };

/** Rules of one plan category, on top of the rules every plan follows. Omitted rules are free. */
export type PlanDefinition = {
  subPlans?: {
    // The plan can only be made of these sub-plans
    allowed?: readonly B2bSavingPlanType[];
    required?: readonly B2bSavingPlanType[];
    // AXA migration imports (requests with id_external) may hold only some of the required sub-plans
    allowsIncompleteMigratedPlans?: true;
  };
  // Replace the default names, which refer to AXA offers
  names?: Partial<Record<B2bSavingPlanCategory | B2bSavingPlanType, string>>;
  // Plans can only allow these ones, possibly none
  cashSources?: readonly B2bCashSource[];
  matchingContributionRules?: readonly MatchingContributionConfig['type'][];
  matchingContributionPoolType?: FieldRule<(typeof B2bCompanyPlanMCPoolType)[number]>;
  maxEntryFeePct?: number;
  rebalancingFrequency?: FieldRule<B2bManagedPlanRebalancingFrequency>;
  minimumTenureMonths?: FieldRule<number>;
  // Funds gestion libre sub-plans can offer: the BO prefills all of them, plans can then remove some
  instrumentsUniverseIsins?: readonly string[];
  // Funds the BO does not let users remove from the instruments universe
  requiredInstrumentsUniverseIsins?: readonly string[];
  // Replaces the default fund of the contract type. A locked one cannot be replaced by plans.
  defaultFundIsin?: FieldRule<string>;
};

/**
 * What differs between contract types. Code reads it through the definition instead of branching
 * on contract type names, so adding a contract type means adding its definition here.
 */
export type ContractTypeDefinition = {
  // Employee savings ("épargne salariale") or retirement savings ("épargne retraite")
  family: 'es' | 'er';
  // Run by an external insurer: no fees nor distributor, and campaigns handled by the distributor
  isExternal: boolean;
  // Only this insurer's environments, besides Shares ones, can create such contracts
  restrictedToInsurer: 'axa' | 'malakoff' | null;
  planCategories: readonly B2bSavingPlanCategory[];
  contract: {
    // The offer's name and support line: the BO fills them in on creation and does not let users
    // change them
    label?: FieldRule<string>;
    supportPhoneNumber?: FieldRule<string>;
    isLiv?: FieldRule<boolean>;
    totalContributionCeiling?: FieldRule<number | null>;
    relationshipType?: FieldRule<(typeof B2bContractRelationshipTypeValues)[number]>;
    filibUrl?: FieldRule<string | null>;
  };
  // Standard custody fees, or amounts set contract by contract (per employee status, per plan…)
  custodyFees: 'standard' | 'custom';
  campaigns: {
    // Allocation, choice collection and payout all run by the company or all by the account keeper
    hasSingleHandler: boolean;
    // Each cash source can have its own processing options, instead of the same ones for all
    allowsProcessingOptionsByCashSource: boolean;
    // Stored whatever the request says: the account keeper cannot compute CET allocations
    // (BRAV-10530), and other contracts have the same options on every cash source
    unusedVacationDaysAllocationComputedBy?: B2bCampaignActionHandler;
    defaultMatchingContributionComputedBy?: B2bCampaignActionHandler;
  };
  employees: {
    // The BO lets users create employees without onboarding them automatically
    allowsManualOnboarding: boolean;
  };
  transfers: {
    // Collective transfers into such contracts can be edited (calendar included)
    allowsCollectiveTransferInEdits: boolean;
  };
  // Fund invested in when employees make no choice, for plans that do not pick their own. None when
  // omitted.
  defaultFundIsin?: ByEnvironmentInsurer<string>;
  // Funds the BO prefills the instruments universe of new plans with, when their plan definition does
  // not restrict it (instrumentsUniverseIsins)
  instrumentsUniversePrefillIsins?: ByEnvironmentInsurer<readonly string[]>;
  // Where managed sub-plans take their allocation grids from: the generic grids of the contract type,
  // all linked on creation, or the contract's own ones picked in the BO. None for plans managed by
  // Taiga.
  riskProfileGrids: 'generic' | 'custom' | null;
  // What the BO plan form lets users configure, for company plans set up one by one ("sur-mesure")
  planSettings: {
    // PEE, PERECO… plans pick their sub-plans, and can get the missing one later
    allowsSubPlanChoice: boolean;
    // Gestion libre sub-plans pick their default fund
    allowsDefaultFundChoice: boolean;
    // Each fund can accept its own cash sources
    allowsCashSourcesPerInstrument: boolean;
    // Matching contributions can depend on the employees' socio-professional category
    allowsCspMatchingContributionRules: boolean;
    // Plans pick their default grid among the generic grids of the contract type, which can offer
    // several grids of the same risk profile. Otherwise it is the generic grid of the default risk
    // profile.
    allowsDefaultGenericGridChoice: boolean;
  };
  // Only for categories with rules, which must be among planCategories
  plans: Partial<Record<B2bSavingPlanCategory, PlanDefinition>>;
};

// Rules shared by both plans of the Malakoff Humanis ESSENTIEL offer
const ESSENTIEL_PLAN_RULES = {
  cashSources: [
    'voluntary_deposit',
    'participation',
    'profit_sharing',
    'value_sharing_bonus',
    'unused_vacation_days',
  ],
  // Simple (company_simple when computed by the company), tiered and tiered by cash source
  matchingContributionRules: [
    'simple',
    'company_simple',
    'limit_brackets',
    'limit_brackets_per_cash_source',
  ],
  matchingContributionPoolType: { default: 'cumulative_contributions', locked: true },
  maxEntryFeePct: 4,
  minimumTenureMonths: { default: 3, locked: true },
  // Malakoff Humanis "Coeur" range, L shares
  instrumentsUniverseIsins: MALAKOFF_L_CORE_FUND_ISINS,
} as const satisfies PlanDefinition;

// Values fixed on every contract of the Malakoff Humanis company offers
const MALAKOFF_COMPANY_OFFER_CONTRACT_RULES = {
  // Filib is an AXA tool
  filibUrl: { default: null, locked: true },
  isLiv: { default: true, locked: true },
} as const satisfies ContractTypeDefinition['contract'];

// Rules shared by the plans of the Malakoff Humanis company offers (FLEXIBLE, PATRIMONIAL, PREMIUM
// and sur mesure), each offer setting its funds and seniority on top
const MALAKOFF_COMPANY_OFFER_PLAN_RULES = {
  cashSources: [
    'voluntary_deposit',
    'participation',
    'profit_sharing',
    'value_sharing_bonus',
    'unused_vacation_days',
  ],
  matchingContributionRules: [
    'simple',
    'company_simple',
    'limit_brackets',
    'limit_brackets_per_cash_source',
    'seniority_brackets',
  ],
  matchingContributionPoolType: { default: 'cumulative_contributions', locked: true },
  maxEntryFeePct: 0,
} as const satisfies PlanDefinition;

const MALAKOFF_COMPANY_OFFER_PEE = {
  subPlans: { allowed: ['PEE_LIBRE'], required: ['PEE_LIBRE'] },
  names: { PEE: 'PEE - Libre' },
} as const satisfies PlanDefinition;

const MALAKOFF_COMPANY_OFFER_PERECO = {
  subPlans: {
    allowed: ['PERECO_LIBRE', 'PERECO_PILOTE'],
    required: ['PERECO_LIBRE', 'PERECO_PILOTE'],
  },
  rebalancingFrequency: { default: '6_months', locked: true },
} as const satisfies PlanDefinition;

const FLEXIBLE_PLAN_RULES = {
  ...MALAKOFF_COMPANY_OFFER_PLAN_RULES,
  minimumTenureMonths: { default: 3 },
  instrumentsUniverseIsins: [...MALAKOFF_M_FUND_ISINS, ...MALAKOFF_EXTERNAL_FUND_ISINS],
  requiredInstrumentsUniverseIsins: [MALAKOFF_M_DEFAULT_FUND_ISIN],
  defaultFundIsin: { default: MALAKOFF_M_DEFAULT_FUND_ISIN },
} as const satisfies PlanDefinition;

const PATRIMONIAL_PLAN_RULES = {
  ...MALAKOFF_COMPANY_OFFER_PLAN_RULES,
  // Up to 4% for the Sienna funds, 3% for the others
  maxEntryFeePct: 4,
  minimumTenureMonths: { default: 3 },
  instrumentsUniverseIsins: [...MALAKOFF_L_FUND_ISINS, ...MALAKOFF_EXTERNAL_FUND_ISINS],
  defaultFundIsin: { default: MALAKOFF_L_DEFAULT_FUND_ISIN },
} as const satisfies PlanDefinition;

const PREMIUM_PLAN_RULES = {
  ...MALAKOFF_COMPANY_OFFER_PLAN_RULES,
  minimumTenureMonths: { default: 3 },
  instrumentsUniverseIsins: [...MALAKOFF_H_FUND_ISINS, ...MALAKOFF_EXTERNAL_FUND_ISINS],
  defaultFundIsin: { default: MALAKOFF_H_DEFAULT_FUND_ISIN },
} as const satisfies PlanDefinition;

// Any share class, so no default fund
const SUR_MESURE_PLAN_RULES = {
  ...MALAKOFF_COMPANY_OFFER_PLAN_RULES,
  matchingContributionRules: [
    'simple',
    'company_simple',
    'limit_brackets',
    'limit_brackets_per_cash_source',
  ],
  instrumentsUniverseIsins: [
    ...MALAKOFF_M_FUND_ISINS,
    ...MALAKOFF_EXTERNAL_FUND_ISINS,
    ...MALAKOFF_L_FUND_ISINS,
    ...MALAKOFF_H_FUND_ISINS,
  ],
  requiredInstrumentsUniverseIsins: [MALAKOFF_M_DEFAULT_FUND_ISIN],
} as const satisfies PlanDefinition;

// Declared in the contract type enum order, which the derived contract type lists follow
export const CONTRACT_TYPE_DEFINITIONS: Record<B2bContractType, ContractTypeDefinition> = {
  soles: {
    family: 'es',
    isExternal: false,
    restrictedToInsurer: 'axa',
    planCategories: ['PEI', 'PERECOI', 'PERCOI'],
    contract: {
      label: { default: 'SOLES' },
      supportPhoneNumber: { default: DEFAULT_COMPANY_SUPPORT_PHONE_NUMBER },
      filibUrl: { default: 'https://app.filib.fr/societe/pLZykKh6vnwLWn2hj8tPx/prestation/soles' },
    },
    custodyFees: 'standard',
    campaigns: {
      hasSingleHandler: false,
      allowsProcessingOptionsByCashSource: false,
    },
    employees: { allowsManualOnboarding: false },
    transfers: { allowsCollectiveTransferInEdits: false },
    defaultFundIsin: AXA_DEFAULT_FUND_ISIN,
    instrumentsUniversePrefillIsins: AXA_PREFILLED_FUND_ISINS,
    riskProfileGrids: 'generic',
    planSettings: {
      allowsSubPlanChoice: false,
      allowsDefaultFundChoice: false,
      allowsCashSourcesPerInstrument: false,
      allowsCspMatchingContributionRules: false,
      allowsDefaultGenericGridChoice: false,
    },
    plans: {
      PEI: {
        subPlans: { required: ['PEI_LIBRE', 'PEI_PROFILE'], allowsIncompleteMigratedPlans: true },
        defaultFundIsin: { default: AXA_DEFAULT_FUND_ISIN, locked: true },
      },
      PERECOI: {
        subPlans: { required: ['PERECOI_LIBRE', 'PERECOI_PILOTE'] },
        requiredInstrumentsUniverseIsins: AXA_PERECOI_REQUIRED_FUND_ISINS,
      },
    },
  },
  es_custom: {
    family: 'es',
    isExternal: false,
    restrictedToInsurer: null,
    planCategories: ['PEE', 'PEEG', 'PEI', 'PERECO', 'PERECOI', 'PERECOG', 'PERCO', 'PERCOI'],
    contract: {},
    custodyFees: 'custom',
    campaigns: {
      hasSingleHandler: false,
      allowsProcessingOptionsByCashSource: true,
      unusedVacationDaysAllocationComputedBy: 'company',
    },
    employees: { allowsManualOnboarding: true },
    transfers: { allowsCollectiveTransferInEdits: true },
    defaultFundIsin: { axa: AXA_DEFAULT_FUND_ISIN, malakoff: MALAKOFF_L_DEFAULT_FUND_ISIN },
    instrumentsUniversePrefillIsins: {
      axa: AXA_PREFILLED_FUND_ISINS,
      malakoff: MALAKOFF_L_CORE_FUND_ISINS,
    },
    riskProfileGrids: 'custom',
    planSettings: {
      allowsSubPlanChoice: true,
      allowsDefaultFundChoice: true,
      allowsCashSourcesPerInstrument: true,
      allowsCspMatchingContributionRules: true,
      allowsDefaultGenericGridChoice: false,
    },
    plans: {
      PERECOI: { requiredInstrumentsUniverseIsins: AXA_PERECOI_REQUIRED_FUND_ISINS },
    },
  },
  er_standard: {
    family: 'er',
    isExternal: false,
    restrictedToInsurer: 'axa',
    planCategories: ['PER', 'ARTICLE_82', 'ARTICLE_83'],
    contract: {
      label: { default: 'SOLERE' },
      supportPhoneNumber: { default: DEFAULT_COMPANY_SUPPORT_PHONE_NUMBER },
    },
    custodyFees: 'standard',
    campaigns: {
      hasSingleHandler: false,
      allowsProcessingOptionsByCashSource: false,
    },
    employees: { allowsManualOnboarding: false },
    transfers: { allowsCollectiveTransferInEdits: false },
    defaultFundIsin: AXA_DEFAULT_FUND_ISIN,
    instrumentsUniversePrefillIsins: AXA_PREFILLED_FUND_ISINS,
    riskProfileGrids: null,
    planSettings: {
      allowsSubPlanChoice: false,
      allowsDefaultFundChoice: false,
      allowsCashSourcesPerInstrument: false,
      allowsCspMatchingContributionRules: false,
      allowsDefaultGenericGridChoice: false,
    },
    plans: {},
  },
  er_custom: {
    family: 'er',
    isExternal: false,
    restrictedToInsurer: 'axa',
    planCategories: ['PERO', 'PERU', 'ARTICLE_82', 'ARTICLE_83', 'PERECOER'],
    contract: {},
    custodyFees: 'custom',
    campaigns: {
      hasSingleHandler: false,
      allowsProcessingOptionsByCashSource: true,
      unusedVacationDaysAllocationComputedBy: 'company',
    },
    employees: { allowsManualOnboarding: true },
    transfers: { allowsCollectiveTransferInEdits: true },
    defaultFundIsin: AXA_DEFAULT_FUND_ISIN,
    instrumentsUniversePrefillIsins: AXA_PREFILLED_FUND_ISINS,
    riskProfileGrids: 'custom',
    planSettings: {
      allowsSubPlanChoice: true,
      allowsDefaultFundChoice: true,
      allowsCashSourcesPerInstrument: true,
      allowsCspMatchingContributionRules: true,
      allowsDefaultGenericGridChoice: false,
    },
    plans: {},
  },
  er_external: {
    family: 'er',
    isExternal: true,
    restrictedToInsurer: 'axa',
    planCategories: ['ER_EXTERNAL'],
    contract: {},
    custodyFees: 'standard',
    campaigns: {
      hasSingleHandler: false,
      allowsProcessingOptionsByCashSource: false,
    },
    employees: { allowsManualOnboarding: false },
    transfers: { allowsCollectiveTransferInEdits: false },
    instrumentsUniversePrefillIsins: AXA_PREFILLED_FUND_ISINS,
    riskProfileGrids: null,
    planSettings: {
      allowsSubPlanChoice: false,
      allowsDefaultFundChoice: false,
      allowsCashSourcesPerInstrument: false,
      allowsCspMatchingContributionRules: false,
      allowsDefaultGenericGridChoice: false,
    },
    plans: {},
  },
  essentiel: {
    family: 'es',
    isExternal: false,
    restrictedToInsurer: 'malakoff',
    planCategories: ['PEI', 'PERECOI'],
    contract: {
      label: { default: 'OFFRE ESSENTIEL', locked: true },
      relationshipType: { default: 'direct', locked: true },
      // Filib is an AXA tool
      filibUrl: { default: null, locked: true },
      isLiv: { default: true, locked: true },
      // No contract-wide ceiling: any value would cap every matching contribution
      totalContributionCeiling: { default: null, locked: true },
    },
    custodyFees: 'standard',
    campaigns: {
      hasSingleHandler: true,
      allowsProcessingOptionsByCashSource: false,
      defaultMatchingContributionComputedBy: 'distributor',
    },
    employees: { allowsManualOnboarding: false },
    transfers: { allowsCollectiveTransferInEdits: false },
    defaultFundIsin: MALAKOFF_L_DEFAULT_FUND_ISIN,
    riskProfileGrids: 'generic',
    planSettings: {
      allowsSubPlanChoice: false,
      allowsDefaultFundChoice: false,
      allowsCashSourcesPerInstrument: false,
      allowsCspMatchingContributionRules: false,
      allowsDefaultGenericGridChoice: false,
    },
    plans: {
      PEI: {
        ...ESSENTIEL_PLAN_RULES,
        subPlans: { allowed: ['PEI_LIBRE'], required: ['PEI_LIBRE'] },
        names: { PEI: 'PEI - Libre', PEI_LIBRE: 'PEI - Libre' },
        defaultFundIsin: { default: MALAKOFF_L_DEFAULT_FUND_ISIN, locked: true },
      },
      PERECOI: {
        ...ESSENTIEL_PLAN_RULES,
        subPlans: {
          allowed: ['PERECOI_LIBRE', 'PERECOI_PILOTE'],
          required: ['PERECOI_LIBRE', 'PERECOI_PILOTE'],
        },
        names: {
          PERECOI: 'PERECO-I - (Piloté & Libre)',
          PERECOI_LIBRE: 'PERECO-I - Libre',
          PERECOI_PILOTE: 'PERECO-I - Piloté',
        },
        rebalancingFrequency: { default: '6_months', locked: true },
      },
    },
  },
  flexible: {
    family: 'es',
    isExternal: false,
    restrictedToInsurer: 'malakoff',
    planCategories: ['PEE', 'PERECO'],
    contract: {
      ...MALAKOFF_COMPANY_OFFER_CONTRACT_RULES,
      label: { default: 'OFFRE FLEXIBLE', locked: true },
      totalContributionCeiling: { default: null },
    },
    custodyFees: 'custom',
    campaigns: {
      // Companies can delegate only some campaign steps to the account keeper
      hasSingleHandler: false,
      allowsProcessingOptionsByCashSource: true,
      unusedVacationDaysAllocationComputedBy: 'company',
    },
    employees: { allowsManualOnboarding: true },
    transfers: { allowsCollectiveTransferInEdits: true },
    riskProfileGrids: 'generic',
    planSettings: {
      allowsSubPlanChoice: true,
      allowsDefaultFundChoice: true,
      allowsCashSourcesPerInstrument: true,
      allowsCspMatchingContributionRules: true,
      allowsDefaultGenericGridChoice: true,
    },
    plans: {
      PEE: { ...FLEXIBLE_PLAN_RULES, ...MALAKOFF_COMPANY_OFFER_PEE },
      PERECO: { ...FLEXIBLE_PLAN_RULES, ...MALAKOFF_COMPANY_OFFER_PERECO },
    },
  },
  patrimonial: {
    family: 'es',
    isExternal: false,
    restrictedToInsurer: 'malakoff',
    planCategories: ['PEE', 'PERECO'],
    contract: {
      ...MALAKOFF_COMPANY_OFFER_CONTRACT_RULES,
      label: { default: 'OFFRE PATRIMONIAL', locked: true },
      totalContributionCeiling: { default: null },
    },
    custodyFees: 'custom',
    campaigns: {
      hasSingleHandler: true,
      allowsProcessingOptionsByCashSource: true,
      unusedVacationDaysAllocationComputedBy: 'company',
    },
    employees: { allowsManualOnboarding: true },
    transfers: { allowsCollectiveTransferInEdits: true },
    riskProfileGrids: 'generic',
    planSettings: {
      allowsSubPlanChoice: true,
      allowsDefaultFundChoice: true,
      allowsCashSourcesPerInstrument: true,
      allowsCspMatchingContributionRules: true,
      allowsDefaultGenericGridChoice: true,
    },
    plans: {
      PEE: {
        ...PATRIMONIAL_PLAN_RULES,
        ...MALAKOFF_COMPANY_OFFER_PEE,
        requiredInstrumentsUniverseIsins: [MALAKOFF_L_DEFAULT_FUND_ISIN],
      },
      PERECO: { ...PATRIMONIAL_PLAN_RULES, ...MALAKOFF_COMPANY_OFFER_PERECO },
    },
  },
  premium: {
    family: 'es',
    isExternal: false,
    restrictedToInsurer: 'malakoff',
    planCategories: ['PEE', 'PERECO'],
    contract: {
      ...MALAKOFF_COMPANY_OFFER_CONTRACT_RULES,
      label: { default: 'OFFRE PREMIUM', locked: true },
      totalContributionCeiling: { default: null },
    },
    custodyFees: 'custom',
    campaigns: {
      hasSingleHandler: true,
      allowsProcessingOptionsByCashSource: true,
      unusedVacationDaysAllocationComputedBy: 'company',
    },
    employees: { allowsManualOnboarding: true },
    transfers: { allowsCollectiveTransferInEdits: true },
    riskProfileGrids: 'generic',
    planSettings: {
      allowsSubPlanChoice: true,
      allowsDefaultFundChoice: true,
      allowsCashSourcesPerInstrument: true,
      allowsCspMatchingContributionRules: true,
      allowsDefaultGenericGridChoice: true,
    },
    plans: {
      PEE: { ...PREMIUM_PLAN_RULES, ...MALAKOFF_COMPANY_OFFER_PEE },
      PERECO: { ...PREMIUM_PLAN_RULES, ...MALAKOFF_COMPANY_OFFER_PERECO },
    },
  },
  sur_mesure: {
    family: 'es',
    isExternal: false,
    restrictedToInsurer: 'malakoff',
    planCategories: ['PEE', 'PERECO'],
    contract: {
      ...MALAKOFF_COMPANY_OFFER_CONTRACT_RULES,
      label: { default: 'Sur mesure', locked: true },
      totalContributionCeiling: { default: null, locked: true },
    },
    custodyFees: 'custom',
    campaigns: {
      hasSingleHandler: true,
      allowsProcessingOptionsByCashSource: true,
      unusedVacationDaysAllocationComputedBy: 'company',
      defaultMatchingContributionComputedBy: 'distributor',
    },
    employees: { allowsManualOnboarding: true },
    transfers: { allowsCollectiveTransferInEdits: true },
    riskProfileGrids: 'custom',
    planSettings: {
      allowsSubPlanChoice: true,
      allowsDefaultFundChoice: true,
      allowsCashSourcesPerInstrument: true,
      allowsCspMatchingContributionRules: true,
      allowsDefaultGenericGridChoice: false,
    },
    plans: {
      PEE: {
        ...SUR_MESURE_PLAN_RULES,
        ...MALAKOFF_COMPANY_OFFER_PEE,
        requiredInstrumentsUniverseIsins: [
          MALAKOFF_M_DEFAULT_FUND_ISIN,
          MALAKOFF_L_DEFAULT_FUND_ISIN,
        ],
      },
      PERECO: { ...SUR_MESURE_PLAN_RULES, ...MALAKOFF_COMPANY_OFFER_PERECO },
    },
  },
};

export const getContractTypeDefinition = (contractType: B2bContractType): ContractTypeDefinition =>
  CONTRACT_TYPE_DEFINITIONS[contractType];

/** Contract types whose definition matches, in the contract type enum order */
export const getContractTypesWhere = (
  predicate: (definition: ContractTypeDefinition) => boolean,
): B2bContractType[] =>
  (Object.keys(CONTRACT_TYPE_DEFINITIONS) as B2bContractType[]).filter((contractType) =>
    predicate(CONTRACT_TYPE_DEFINITIONS[contractType]),
  );
